<script lang="ts" setup>
import { Company } from '@/domains/models/Company';
// import { getGmailLink, getGoogleCalendarEventLink, getWhatsappLink } from '@/helpers/quicklinks';
import { ref, defineEmits, defineProps, withDefaults, onMounted, computed } from 'vue';
// import { useI18n } from '@/i18n/i18n';

import { getConfiguration } from '@/api/emails';

import BcAvatar from '@/ui-kit/components/BcAvatar/BcAvatar.vue';
import imgFallback from '@/assets/img/default-avatar-company-2.svg';

import linkedin from '@/assets/logo/linkedin.png';
// import { useRoute, useRouter } from 'vue-router/composables';
import { useStore } from '@/store';
import BcButton from '@/ui-kit/components/BcButton';
import { Employee } from '@/domains/models/Employee';

// const i18n = useI18n();
// const route = useRoute();
// const router = useRouter();
const store = useStore();

interface Props {
  company: Company;
  employees: Employee[];
}

const props = withDefaults(defineProps<Props>(), {
  company: () => ({}) as Company,
});

defineEmits<{
  (e: 'edit-company', value: boolean): void;
}>();

const isEmailVerified = ref(false);
const companyEmail = computed(() => {
  if (props.company.email) {
    return props.company.email;
  }
  if (props.company.emails?.length) {
    return props.company.emails[0];
  }
  return '';
});

onMounted(async() => {
  const { data: emailConfig } = await getConfiguration();
  isEmailVerified.value = emailConfig.verified;
});

const setAlternativeImg = event => {
  event.target.src = imgFallback;
};

const onSendMail = () => {
  const company = {
    ...props.company,
    _contacts: props.employees,
  }
  store.dispatch('app/setIsPanel', true);
  store.dispatch('app/setParams', { isCompany: true, fromPanel: true });
  store.dispatch('emails/setSelectedCompanies', [company]);
  store.dispatch('app/setModal', 'modal-email-send-form');
}
</script>

<template>
  <div class="relative flex w-full flex-row items-center gap-[18px] bg-white px-2 py-4">
    <BcAvatar
      :src="company?.logoUrl"
      class="size-[80px] shrink-0 rounded-full border-2 border-blue-300"
      @error="setAlternativeImg" />
    <div class="absolute -right-4 top-2 flex gap-2.5">
      <BcButton icon-left="edit-3" size="extra-small" @click="$emit('edit-company', true)" />
    </div>
    <div class="flex w-full flex-col gap-[5px]">
      <a
        :href="
          (company.social || {}).linkedin ||
          `https://www.linkedin.com/search/results/all/?keywords=${company.name}`
        "
        class="hover:underline"
        target="_blank">
        <b class="text-md font-medium text-blue-800">{{ company.name }}</b>
      </a>
      <div class="flex flex-row gap-2.5">
        <a
          :class="{
            'pointer-events-none opacity-50': !company.social?.linkedin,
          }"
          :href="company.social?.linkedin || '#'"
          class="cursor-pointer text-sm"
          target="_blank">
          <div class="flex size-[36px] items-center justify-center rounded-full bg-blue-100">
            <img :src="linkedin" alt="linkedin" class="size-[18px]" />
          </div>
        </a>
        <a
          :class="{ 'pointer-events-none opacity-50': !company.social?.welcomeToTheJungle }"
          :href="company.social?.welcomeToTheJungle"
          class="flex size-[36px] items-center justify-center rounded-full bg-blue-100 p-1 text-sm"
          target="_blank">
          <img alt="wttj" class="size-[18px]" src="@/assets/logo/wttj.png" />
        </a>
        <a
          :class="{ 'pointer-events-none opacity-50': !company.social?.website }"
          :href="company.social?.website"
          class="flex size-[36px] items-center justify-center rounded-full bg-blue-100 text-sm text-blue-800 hover:text-blue-500"
          target="_blank">
          <i class="icon-website text-[16px]" />
        </a>
        <div
          v-if="isEmailVerified"
          class="flex size-[36px] items-center justify-center rounded-full bg-blue-100">
          <button
            :class="{
              'pointer-events-none opacity-50': !companyEmail,
            }"
            @click="onSendMail">
            <svg
              fill="none"
              height="37"
              viewBox="0 0 37 37"
              width="37"
              xmlns="http://www.w3.org/2000/svg">
              <circle cx="18.0068" cy="18.0068" fill="#EDF0FF" r="18.0068" />
              <path
                clip-rule="evenodd"
                d="M26.5893 11H10.4102L10.2642 11.0509L10.2642 11.0509C10.1676 11.0845 10.0684 11.1191 9.96143 11.1565C9.97405 11.1719 9.98538 11.1862 9.99601 11.1997L9.99602 11.1997L9.99602 11.1997C10.0186 11.2282 10.038 11.2528 10.06 11.2748C12.4574 13.6595 14.8551 16.0439 17.2544 18.4272C17.7515 18.9209 18.3434 19.1139 19.0156 18.8819C19.2744 18.7927 19.5313 18.628 19.7269 18.435C21.6651 16.5215 23.5962 14.6006 25.5272 12.6798L25.5273 12.6796L25.5278 12.6792L25.5369 12.6701C26.0089 12.2006 26.4809 11.7311 26.9529 11.2618C26.9709 11.2438 26.9877 11.2248 27.0106 11.1989L27.0106 11.1989L27.044 11.1614L26.8108 11.0786L26.5893 11ZM9.16136 11.9468L9 12.4089V22.9392C9.05168 23.0853 9.10336 23.2311 9.17289 23.427L10.5625 22.0108L10.572 22.0011L10.5811 21.9918C12.0145 20.531 13.4257 19.0926 14.7983 17.6934L11.9487 14.7883L9.16136 11.9468ZM9.99561 24.2407L9.99624 24.2401C10.0679 24.2503 10.142 24.2639 10.2178 24.2778C10.3874 24.3089 10.5657 24.3416 10.744 24.3418C14.0462 24.3473 17.3481 24.3471 20.6501 24.3468H20.6501L22.8036 24.3467C23.113 24.3467 23.4224 24.3471 23.7318 24.3476H23.7321C24.5915 24.3489 25.4509 24.3502 26.3101 24.3415C26.48 24.3396 26.6496 24.3039 26.7968 24.2729C26.8576 24.2601 26.9146 24.2481 26.9663 24.2396L24.6868 21.9158L21.2862 18.4491C21.2052 18.5305 21.1263 18.6102 21.0488 18.6886C20.8642 18.8751 20.6871 19.0541 20.5073 19.2303C19.451 20.2639 17.9455 20.3799 16.8011 19.4961C16.5705 19.3181 16.3645 19.1076 16.1588 18.8975L16.1588 18.8975L16.1588 18.8974C16.0913 18.8285 16.0239 18.7596 15.9556 18.6919C15.9047 18.6414 15.8635 18.5809 15.8152 18.5097L15.8152 18.5097L15.8152 18.5097C15.7905 18.4734 15.764 18.4344 15.7333 18.3926C13.8645 20.2971 12.0503 22.1463 10.2204 24.0116L10.2072 24.025L9.99624 24.2401L9.99561 24.24V24.2407ZM27.8106 11.9302L22.1874 17.6619C23.3527 18.8502 24.5312 20.0516 25.7144 21.2578C26.4119 21.9689 27.1111 22.6817 27.8102 23.3944C27.8347 23.3896 27.8593 23.3844 27.8838 23.3792C27.8959 23.3009 27.9112 23.2227 27.9264 23.1445C27.9592 22.9771 27.9919 22.8097 27.9931 22.6419C28.0009 21.558 28 20.4741 27.999 19.3902C27.9987 19.0289 27.9983 18.6676 27.9983 18.3063C27.9983 17.932 27.9983 17.5578 27.9983 17.1835C27.9982 15.6863 27.9982 14.1891 27.9991 12.692C27.9991 12.4384 27.9545 12.1979 27.8106 11.9302Z"
                fill="url(#paint0_linear_848_47423)"
                fill-rule="evenodd" />
              <defs>
                <linearGradient
                  id="paint0_linear_848_47423"
                  gradientUnits="userSpaceOnUse"
                  x1="9"
                  x2="27.9996"
                  y1="17.6743"
                  y2="17.6743">
                  <stop stop-color="#F71E00" />
                  <stop offset="1" stop-color="#F78E00" />
                </linearGradient>
              </defs>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
